import React, { SyntheticEvent } from "react";

import type { ChangeHandler } from "react-hook-form";

import FormControl from "@mui/material/FormControl";
import MuiFormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import * as PolicyUtils from "@app/common/utils/policyUtils";

import Checkbox from "@rui/Checkbox";
import Link from "@rui/Link";
import Stack from "@rui/Stack";

interface ExplicitPolicyConsentProps {
  name: string;
  consented: boolean;
  errorMessage?: string;
  onChange: ChangeHandler;
}

const Wrapper = styled(Stack)(({ theme }) => ({
  maxWidth: "390px",
  minHeight: "42px",
  marginBottom: theme.spacing(2),
}));

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  alignItems: "flex-start",
  margin: theme.spacing(2, 0, 0, -1),
  maxWidth: "400px",
  "& .MuiFormControlLabel-label": {
    paddingTop: "6px",
    lineHeight: 18 / 14,
  },
  "&.Mui-error .checkbox__icon": {
    borderColor: theme.palette.error.main,
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  ...theme.typography.bodySecondaryReg,
  color: theme.palette.shades.neutral[500],
}));

const ExplicitPolicyConsent = React.forwardRef<
  HTMLElement,
  ExplicitPolicyConsentProps
>(({ name, consented, onChange, errorMessage }, ref) => {
  const isError = !consented && Boolean(errorMessage);
  const handleChange = (event: SyntheticEvent, checked: boolean) => {
    return onChange({ target: { name, value: checked } });
  };

  return (
    <Wrapper>
      <FormControl error={isError}>
        <FormControlLabel
          ref={ref}
          checked={consented}
          control={
            <Checkbox
              data-test-id="auth-accept-terms"
              name={name}
              variant="filled"
            />
          }
          label={
            <Label>
              I have read and agree to Read's{" "}
              <Link href={PolicyUtils.newUserPrivacyPolicyLink()}>
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link href={PolicyUtils.newUserTermsOfServiceLink()}>
                Terms of Service
              </Link>
            </Label>
          }
          onChange={handleChange}
        />
        {isError && (
          <FormHelperText className="policy-accepted-error">
            {errorMessage}
          </FormHelperText>
        )}
      </FormControl>
    </Wrapper>
  );
});

ExplicitPolicyConsent.displayName = "ExplicitPolicyConsent";

export default ExplicitPolicyConsent;
